import { Fragment, createElement, useEffect, useMemo, useRef } from 'react';

import { GridRenderCellParams, GridValidRowModel } from '@mui/x-data-grid-premium';

import { Dayjs } from 'dayjs';

import { GeneratikTableColumn } from '../GeneratikTable.types';
import AxisBoundaryHeader from '../timeline/AxisBoundaryHeader';
import AxisGapHeader from '../timeline/AxisGapHeader';
import AxisGridlineHoverTarget from '../timeline/AxisGridlineHoverTarget';
import TimelinePeriodCell from '../timeline/TimelinePeriodCell';
import {
  AxisLabel,
  BookingsAxis,
  EDGE_COLUMN_WIDTH,
  GAP_CAPTION_MIN_WIDTH,
  buildBookingsAxis,
  offsetForDateOnAxis,
  resolveAxisLabels,
} from '../timeline/bookingsAxis';
import {
  TimelineAxis,
  TimelineBooking,
  TimelineGranularity,
  TimelineRange,
} from '../timeline/timeline.types';
import {
  buildTimelineRange,
  collectBookingBounds,
  formatPeriodHeader,
  getPeriodFieldName,
  offsetForDate,
  timelinePeriodColumnWidth,
  toDayjs,
} from '../timeline/timeline.utils';
import { useFusedRowBookings } from '../timeline/useFusedRowBookings';

/**
 * What a header needs to draw one resolved boundary label, shared by all three that can carry one
 * — an anchor's own header, a break's (whose date is the booking that ended before the gap), and
 * the trailing column's, passed as `idx === segments.length`.
 *
 * Every one of them can answer a hover mark, so `field` is unconditional: an anchor lights the
 * gridline under its date, a break lights its own band instead (AXIS_BREAK_BAND_CLASS).
 *
 * `x0` is the boundary's rail-global position: the resolver centers labels in rail coordinates,
 * while a header cell only knows its own (left: 0 = its own left edge), so the difference is what
 * actually gets rendered — see AxisBoundaryHeader's own doc for why that reference frame is exact.
 */
const boundaryLabelProps = (label: AxisLabel | undefined, x0: number, idx: number) => ({
  left: label,
  field: getPeriodFieldName(idx),
  // A merged label speaks for a second boundary too (pairedIdx — the trailing column when it's
  // one past the segments), and hover marks both.
  pairedField: label?.pairedIdx != null ? getPeriodFieldName(label.pairedIdx) : undefined,
  labelOffsetX: label ? label.centerX - x0 : undefined,
});

interface UseTimelineColumnsOptions<R extends GridValidRowModel> {
  enabled: boolean;
  userColumns: GeneratikTableColumn<R>[];
  rows: R[];
  granularity: TimelineGranularity;
  axis?: TimelineAxis;
  getRowBookings?: (row: R) => TimelineBooking[];
  onBarClick?: (booking: TimelineBooking, event: React.MouseEvent) => void;
  timelineMinDate?: Dayjs;
  timelineMaxDate?: Dayjs;
  highlightRange?: { start?: Dayjs; end?: Dayjs };
  /**
   * The bookings axis's own outer window — the campaign period on order/media-plan rails. Takes
   * priority over timelineMinDate/timelineMaxDate for the axis's boundaries, which on those rails
   * carry display margin instead of the campaign's real edges. @see GeneratikTableProps.timelineAxisWindow
   */
  axisWindowStart?: Dayjs;
  axisWindowEnd?: Dayjs;
  /** @see GeneratikTableProps.timelineAxisWindowMode — 'clamp' makes the window a hard bound. */
  axisWindowMode?: 'extend' | 'clamp';
  /**
   * The bookings axis's protected range — empty stretches inside it never compress into break
   * columns. Deliberately independent from `highlightRange`: the highlight is only ever the
   * visual band, and on rails where that band is the rail's own campaign period, protecting it
   * would forbid compression across the whole rail. @see GeneratikTableProps.timelineAxisProtectRange
   */
  axisProtectStart?: Dayjs;
  axisProtectEnd?: Dayjs;
  /** @see buildTimelineRange – minimum number of period columns to show. Defaults to 4. */
  minPeriods?: number;
  /** @see GeneratikTableProps.timelineDataPending */
  dataPending?: boolean;
}

interface UseTimelineColumnsResult<R extends GridValidRowModel> {
  columns: GeneratikTableColumn<R>[];
  pinnedFields: string[];
  range: TimelineRange | null;
  dateToOffset: ((date: Dayjs) => number) | null;
  /**
   * The axis actually drawn, which can differ from the requested `axis` input: the bookings
   * branch falls back to calendar columns when nothing renders (`buildBookingsAxis` returns
   * null) — either no row has a rendered bar and no period is protected, or subdivision pushed
   * the column count past MAX_AXIS_COLUMNS (far more reachable now than under v1's
   * one-column-per-span model, since a long span slices into many columns instead of staying
   * one). Callers that gate UI on the axis (the granularity toggle, the scroll hook's scale key)
   * must key off this, not the input — keying off the input hides the toggle on a rail that is
   * visibly drawing calendar columns, with no way to switch granularity back.
   */
  axis: TimelineAxis;
}

const useTimelineColumns = <R extends GridValidRowModel>({
  enabled,
  userColumns,
  rows,
  granularity,
  axis = 'calendar',
  getRowBookings,
  onBarClick,
  timelineMinDate,
  timelineMaxDate,
  highlightRange,
  axisWindowStart,
  axisWindowEnd,
  axisWindowMode,
  axisProtectStart,
  axisProtectEnd,
  minPeriods = 4,
  dataPending,
}: UseTimelineColumnsOptions<R>): UseTimelineColumnsResult<R> => {
  // Bars that meet at a day boundary are fused once here, across every row, so the axis and
  // every cell draw them touching — see useFusedRowBookings. On the bookings axis the window and
  // the protected period open lines of their own, so a bar ending the day before meets those too.
  const rowBookings = useFusedRowBookings(
    rows,
    getRowBookings,
    axis === 'bookings' ? [axisWindowStart ?? toDayjs(timelineMinDate), axisProtectStart] : [],
  );

  // Built on its own memo (rather than inline below) so its identity is a stable dependency: the
  // main memo needs to react to "did a fresh axis build this render", and a ref can't be a
  // dependency of anything.
  const rawBookingsAxis = useMemo(() => {
    if (!enabled || !rowBookings || axis !== 'bookings') return null;
    // Pinned to week regardless of the requested/stored granularity: v5 anchors-only rendering
    // means the toggle has no visible effect on this axis any more (see useTimelineColumns's own
    // `axis` doc and GranularityToggle's gating), so it's hidden — but a stored 'month' value
    // would otherwise still silently make subdivision ~2.2× coarser with no way for the user to
    // change it back. All of the axis's visual calibration (SLICE_MIN_WIDTH, label metrics) was
    // done against week density; pinning keeps the rail deterministic across users/sessions.
    return buildBookingsAxis(rows, rowBookings, {
      windowStart: axisWindowStart ?? toDayjs(timelineMinDate),
      windowEnd: axisWindowEnd ?? toDayjs(timelineMaxDate),
      granularity: 'week',
      protectedStart: axisProtectStart,
      protectedEnd: axisProtectEnd,
      clampToWindow: axisWindowMode === 'clamp',
    });
  }, [
    enabled,
    rowBookings,
    axis,
    rows,
    axisWindowStart,
    timelineMinDate,
    axisWindowEnd,
    timelineMaxDate,
    axisWindowMode,
    axisProtectStart,
    axisProtectEnd,
  ]);

  const lastBookingsAxisRef = useRef<BookingsAxis | undefined>(undefined);

  // Holds the axis across a refetch: an occupancy query that resets to synthetic AVAILABLE-only
  // filler bars mid-flight makes buildBookingsAxis return null for a render or two, and without
  // this the rail would flip to the calendar fallback and back the moment real data lands,
  // re-centering the caller's scroll position on every pagination/filter/sort round-trip.
  //
  // Written from an effect, never during render: a render can be thrown away before it commits
  // (React 18 Strict Mode deliberately re-invokes render — including useMemo factories — to
  // surface exactly this kind of impurity), and a ref write inside the memo would leak from a
  // render that never became visible. An effect only ever runs after its render has committed, so
  // by the time the next render's memo reads lastBookingsAxisRef.current, it reflects the axis
  // that was actually last shown — the same "read during render, write in an effect" split the
  // usePrevious pattern uses.
  useEffect(() => {
    if (rawBookingsAxis) {
      lastBookingsAxisRef.current = rawBookingsAxis;
    } else if (!dataPending) {
      // A genuine (non-pending) null means this row set really has nothing to draw — holding on
      // to a stale axis here would resurrect it on some later, unrelated pending render.
      lastBookingsAxisRef.current = undefined;
    }
  }, [rawBookingsAxis, dataPending]);

  return useMemo(() => {
    if (!enabled || !rowBookings) {
      return {
        columns: userColumns,
        pinnedFields: [],
        range: null,
        dateToOffset: null,
        axis: 'calendar',
      };
    }

    const timelineUserColumns = orderTimelineColumns(userColumns);
    const pinnedFields = timelineUserColumns.map((col) => col.field);

    const periodColumnFlags = {
      sortable: false,
      filterable: false,
      hideable: false,
      disableReorder: true,
      disableColumnMenu: true,
      resizable: false,
    } as const;

    const highlightStart = highlightRange?.start;
    const highlightEnd = highlightRange?.end;

    // Protection comes from axisProtectStart/End alone. A caller that also feeds the same range
    // to the highlight (the spaces-page shape) gets the v2 guarantee — no break column ever
    // overlaps the band — while a highlight-only caller (order/media-plan rails, where the band
    // is the rail's own campaign period) keeps compression inside the band: a break cell simply
    // draws its own band instead of the highlight there (see TimelinePeriodCell's break branch).
    if (axis === 'bookings') {
      // A fetch in flight can make buildBookingsAxis return null for a reason that has nothing to
      // do with the row set actually being empty — see the ref's own comment above.
      const bookingsAxis =
        rawBookingsAxis ?? (dataPending ? lastBookingsAxisRef.current : undefined);

      if (bookingsAxis) {
        const dateToOffset = (date: Dayjs) => offsetForDateOnAxis(date, bookingsAxis);

        // v5 anchors-only (see resolveAxisLabels): only booking/period/window edges (anchors)
        // ever render a date or a row gridline — the weekly/monthly rhythm (ticks) renders
        // nothing at all. Since Round 19 the header itself carries no vertical tick for ANY
        // boundary ("remove the vertical line only from timeline header") — an anchor's line is
        // the full-height row gridline alone, starting at the first data row. Subdivision stays
        // untouched (internal geometry: the slice widths) — only what gets drawn changes here.
        // startKind stays on the segment for the gridline scoping below and for callers that key
        // off it semantically (e.g. tests). The trailing column goes through the same
        // full/day-only/drop ladder as every other anchor, seeded with what the real segments
        // actually placed — it degrades itself rather than ever displacing an earlier label.
        const { labels: segmentLabels, trailingLabel } = resolveAxisLabels(bookingsAxis.segments, {
          x0: bookingsAxis.totalWidth,
          x1: bookingsAxis.totalWidth + EDGE_COLUMN_WIDTH,
          date: bookingsAxis.range.end,
        });

        const segmentColumns: GeneratikTableColumn<R>[] = bookingsAxis.segments.map(
          (segment, idx) => {
            const width = segment.x1 - segment.x0;
            const isAnchor = !segment.isBreak && segment.startKind === 'anchor';
            // A dated break draws a boundary line too: its left edge is where the booking it
            // names stopped, and the date above it needs the same line under it every other
            // header date has. The LEADING break has no date and gets none, exactly as the
            // trailing column does when the rail runs off into a gap — a line under a blank
            // header marks nothing.
            const hasGridline = segment.isBreak
              ? segment.precedingBookingEnd != null
              : segment.startKind === 'anchor';
            const field = getPeriodFieldName(idx);
            const label = segmentLabels[idx];
            const labelProps = boundaryLabelProps(label, segment.x0, idx);
            return {
              field,
              headerName: '',
              renderHeader: () =>
                segment.isBreak
                  ? createElement(AxisGapHeader, {
                      ...labelProps,
                      days: segment.days,
                      showCaption: width >= GAP_CAPTION_MIN_WIDTH,
                    })
                  : createElement(AxisBoundaryHeader, labelProps),
              width,
              minWidth: width,
              maxWidth: width,
              ...periodColumnFlags,
              align: 'center' as const,
              headerAlign: 'center' as const,
              headerClassName: segment.isBreak
                ? 'timeline-period-header timeline-axis-header timeline-axis-gap-header'
                : 'timeline-period-header timeline-axis-header',
              // The --break modifier is what lifts that line above the band the cell also draws
              // (see the z-index rule in GeneratikTable.styles.tsx); it goes on every break,
              // dated or not, since only --anchor decides whether a line renders at all.
              cellClassName: [
                'timeline-axis-cell',
                hasGridline ? 'timeline-axis-cell--anchor' : '',
                segment.isBreak ? 'timeline-axis-cell--break' : '',
              ]
                .filter(Boolean)
                .join(' '),
              // The hover strip comes FIRST so every bar drawn after it wins the hit test where
              // they overlap — see AxisGridlineHoverTarget. Anchors only: a break's band covers
              // its whole cell, so a strip underneath could never be hovered — a gap's line is
              // reached from its header date alone.
              renderCell: (params: GridRenderCellParams<R>) =>
                createElement(
                  Fragment,
                  null,
                  isAnchor
                    ? createElement(AxisGridlineHoverTarget, { key: 'gridline-hover', field })
                    : null,
                  createElement(TimelinePeriodCell, {
                    key: 'period',
                    bookings: rowBookings(params.row),
                    cellLeft: segment.x0,
                    cellRight: segment.x1,
                    dateToOffset,
                    highlightStart,
                    highlightEnd,
                    onBarClick,
                    axisBreakDays: segment.isBreak ? segment.days : undefined,
                  }),
                ),
            };
          },
        );

        // A rail that ends in a break gets no trailing date (resolveAxisLabels suppresses it),
        // so the trailing column must not keep its gridline either: the line would dangle under
        // a blank header, right against the band's own torn right edge, marking a resumption
        // that never comes. Same reasoning as the break columns themselves.
        const endsInBreak = !!bookingsAxis.segments[bookingsAxis.segments.length - 1]?.isBreak;

        const trailingField = getPeriodFieldName(bookingsAxis.segments.length);
        const trailingColumn: GeneratikTableColumn<R> = {
          field: trailingField,
          headerName: '',
          renderHeader: () =>
            createElement(
              AxisBoundaryHeader,
              boundaryLabelProps(
                trailingLabel,
                bookingsAxis.totalWidth,
                bookingsAxis.segments.length,
              ),
            ),
          width: EDGE_COLUMN_WIDTH,
          minWidth: EDGE_COLUMN_WIDTH,
          maxWidth: EDGE_COLUMN_WIDTH,
          ...periodColumnFlags,
          align: 'center' as const,
          headerAlign: 'center' as const,
          headerClassName: 'timeline-period-header timeline-axis-header',
          // The rail's own final boundary keeps the gridline whenever it names a real edge —
          // i.e. everywhere except a rail that trails off into a break (see endsInBreak above).
          cellClassName: endsInBreak
            ? 'timeline-axis-cell'
            : 'timeline-axis-cell timeline-axis-cell--anchor',
          // No bookings reach past the rail's end, so the trailing cell is its gridline's hover
          // strip and nothing else — and carries none at all when there is no gridline to hover.
          renderCell: () =>
            endsInBreak ? null : createElement(AxisGridlineHoverTarget, { field: trailingField }),
        };

        return {
          columns: [...timelineUserColumns, ...segmentColumns, trailingColumn],
          pinnedFields,
          range: bookingsAxis.range,
          dateToOffset,
          axis: 'bookings',
        };
      }
    }

    const { min: bookingsMin, max: bookingsMax } = collectBookingBounds(rows, rowBookings);

    const range = buildTimelineRange({
      bookingsMin,
      bookingsMax,
      minOverride: toDayjs(timelineMinDate),
      maxOverride: toDayjs(timelineMaxDate),
      granularity,
      minPeriods,
    });

    const cellWidth = timelinePeriodColumnWidth(granularity);

    const dateToOffset = (date: Dayjs) => offsetForDate(date, range, granularity, cellWidth);

    const periodColumns: GeneratikTableColumn<R>[] = range.periods.map((period, idx) => ({
      field: getPeriodFieldName(idx),
      headerName: formatPeriodHeader(period, granularity),
      width: cellWidth,
      minWidth: cellWidth,
      maxWidth: cellWidth,
      ...periodColumnFlags,
      align: 'center',
      headerAlign: 'center',
      headerClassName: 'timeline-period-header',
      renderCell: (params: GridRenderCellParams<R>) =>
        createElement(TimelinePeriodCell, {
          bookings: rowBookings(params.row),
          cellLeft: idx * cellWidth,
          cellRight: (idx + 1) * cellWidth,
          dateToOffset,
          highlightStart,
          highlightEnd,
          onBarClick,
        }),
    }));

    return {
      columns: [...timelineUserColumns, ...periodColumns],
      pinnedFields,
      range,
      dateToOffset,
      axis: 'calendar',
    };
  }, [
    enabled,
    userColumns,
    rows,
    granularity,
    axis,
    rowBookings,
    onBarClick,
    timelineMinDate,
    timelineMaxDate,
    highlightRange?.start,
    highlightRange?.end,
    minPeriods,
    rawBookingsAxis,
    dataPending,
  ]);
};

export default useTimelineColumns;

/**
 * The columns pinned beside the rail, in the order they should read.
 *
 * Numbered `timeline` values pin a curated block in its own sequence — the grid's column order
 * is the user's to arrange, and it rarely matches what identifies a row at a glance. Columns
 * flagged with plain `true` keep grid order and follow the numbered ones.
 */
export const orderTimelineColumns = <R extends GridValidRowModel>(
  columns: GeneratikTableColumn<R>[],
): GeneratikTableColumn<R>[] => {
  const numbered = columns
    .filter((col) => typeof col.timeline === 'number')
    .sort((a, b) => (a.timeline as number) - (b.timeline as number));
  const flagged = columns.filter((col) => col.timeline === true);
  return [...numbered, ...flagged];
};
